import { useState, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { RadioButtonsGroup } from '@components/RadioButtonsGroup';
import { Typography } from '@components/Typography';
import { InternalLink } from '@components/Link';
import AddDropdownHeader from '@components/AddDropdownHeader';
import { PhonePreviewFrame } from '@components/PhonePreviewFrame';
import { Tab, isSatisfactionThresholdDisabled } from '@constants/surveys';
import { RoutePath } from '@constants/routes';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { useTranslation } from '@hooks';
import { getSurveyLanguageSet } from '@helpers/surveyLanguages';
import { InfoOutlinedIcon } from 'assets/images/icons';
import Rate from './Rate';
import Positive from './Positive';
import Negative from './Negative';
import Questions from './Questions';

export const PreviewTabs = styled(RadioButtonsGroup)({
    width: 252,
    display: 'block',
    margin: '0 auto 16px',
    '& .MuiFormControlLabel-root': {
        height: 32,
        padding: '0 4px',
    },
});

const ViewMap = {
    Rate: 0,
    Positive: 1,
    Negative: 2,
    SurveyDisabled: 3,
    Questions: 4,
};

const Preview = ({ account, externalId, tab }) => {
    const { t } = useTranslation();
    const [view, setView] = useState(ViewMap.Rate);
    const [selectedPage, setSelectedPage] = useState(0);
    const [visitorGetsSuggestion, setVisitorGetsSuggestion] = useState(true);
    const { watch } = useFormContext();

    const mainLocationId = watch('mainLocationId');
    const locationType = watch('locationType');
    const satisfactionSurveyEnabled = watch('satisfactionSurveyEnabled');
    const isThresholdDisabled = isSatisfactionThresholdDisabled(watch('satisfactionSurveyThreshold'));
    const questions = watch('questions');
    const pages = watch('pages');
    const customTextsEnabled = watch('customTextsEnabled');
    const texts = watch('texts');

    useEffect(() => {
        const currentView = tab === Tab.Questions ? ViewMap.Questions : ViewMap.Rate;
        if (!satisfactionSurveyEnabled && tab !== Tab.Questions) {
            setView(ViewMap.SurveyDisabled);
        } else {
            setView(currentView);
        }
    }, [satisfactionSurveyEnabled, tab]);

    useEffect(() => {
        if (isThresholdDisabled) {
            setView(current => (current === ViewMap.Negative ? ViewMap.Rate : current));
        }
    }, [isThresholdDisabled]);

    useEffect(() => {
        const controller = new AbortController();
        const continueAsVisitor = event => {
            if (event.origin !== import.meta.env.REACT_APP_SURVEY_DOMAIN) return;
            if (event.data?.type !== 'pl-preview-survey-continue') return;
            setVisitorGetsSuggestion(event.data.data?.suggestion !== false);
            setView(event.data.data?.step === 'negative-feedback' ? ViewMap.Negative : ViewMap.Positive);
        };
        window.addEventListener('message', continueAsVisitor, { signal: controller.signal });
        return () => controller.abort();
    }, []);

    // Clamp selectedPage when pages shrink
    useEffect(() => {
        if (pages && pages.length > 1 && selectedPage >= pages.length) {
            setSelectedPage(pages.length - 1);
        } else if (!pages || pages.length <= 1) {
            setSelectedPage(0);
        }
    }, [pages, selectedPage]);

    const previewLocation = useMemo(
        () => account?.companies?.find(c => c.companyId === mainLocationId) || {},
        [mainLocationId, account],
    );

    const questionLanguages = useMemo(
        () => [
            ...new Set(
                Object.values(questions || {})
                    .flat()
                    .map(q => q.language),
            ),
        ],
        [questions],
    );

    const surveyLanguages = useMemo(
        () => getSurveyLanguageSet(customTextsEnabled, texts) || questionLanguages,
        [customTextsEnabled, texts, questionLanguages],
    );
    const wording = useMemo(() => ({ customTextsEnabled, texts }), [customTextsEnabled, texts]);

    const locationSurveyId = `${externalId}${locationType === LOCATION_TYPE.BRAND ? `:${mainLocationId}` : ''}`;
    const hasQuestions = Object.keys(questions).length > 0;
    const showEmptyQuestionsMessage = view === ViewMap.Questions && !hasQuestions;
    const multiplePages = pages && pages.length > 1;

    const pageOptions = useMemo(
        () =>
            (pages || []).map((_, i) => ({
                value: i,
                label: `${t('FeedbackSurveys.page')} ${i + 1}`,
            })),
        [pages, t],
    );

    return (
        <>
            {!showEmptyQuestionsMessage && (
                <Typography
                    variant="caption"
                    sx={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '16px', textAlign: 'center' }}
                >
                    <InfoOutlinedIcon style={{ transform: 'translateY(4px)', marginRight: '8px' }} />
                    <Trans t={t} i18nKey={'FeedbackSurveys.changeAppearance'}>
                        <InternalLink
                            to={RoutePath.AccountSettingsBrands}
                            sx={{ color: 'var(--primary-main)', cursor: 'pointer', textDecoration: 'none' }}
                        >
                            {{ brandSettings: t('FeedbackSurveys.brandSettings') }}
                        </InternalLink>
                    </Trans>
                </Typography>
            )}
            {view !== ViewMap.SurveyDisabled && view !== ViewMap.Questions && (
                <PreviewTabs
                    value={view}
                    onChange={value => {
                        setVisitorGetsSuggestion(true);
                        setView(value);
                    }}
                    options={[
                        { value: ViewMap.Rate, label: t('FeedbackSurveys.main') },
                        { value: ViewMap.Positive, label: t('FeedbackSurveys.positive') },
                        ...(isThresholdDisabled
                            ? []
                            : [{ value: ViewMap.Negative, label: t('FeedbackSurveys.negative') }]),
                    ]}
                />
            )}
            {view === ViewMap.Questions && multiplePages && hasQuestions && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                    <AddDropdownHeader
                        options={pageOptions}
                        onAdd={setSelectedPage}
                        buttonText={`${t('FeedbackSurveys.page')} ${selectedPage + 1}`}
                        buttonProps={{ size: 'small', sx: { backgroundColor: '#fff' } }}
                    />
                </div>
            )}
            {showEmptyQuestionsMessage ? (
                <Typography
                    variant="caption"
                    sx={{ color: 'var(--text-secondary)', display: 'block', marginTop: '16px', textAlign: 'center' }}
                >
                    {t('FeedbackSurveys.addFirstQuestion')}
                </Typography>
            ) : (
                <PhonePreviewFrame>
                    {view === ViewMap.Rate && (
                        <Rate
                            locationSurveyId={locationSurveyId}
                            location={previewLocation}
                            languages={surveyLanguages}
                            wording={wording}
                        />
                    )}
                    {view === ViewMap.Questions && hasQuestions && (
                        <Questions
                            locationSurveyId={locationSurveyId}
                            location={previewLocation}
                            selectedPage={multiplePages ? selectedPage : null}
                            languages={surveyLanguages}
                            wording={wording}
                        />
                    )}
                    {(view === ViewMap.Positive || view === ViewMap.SurveyDisabled) && (
                        <Positive
                            locationSurveyId={locationSurveyId}
                            location={previewLocation}
                            languages={surveyLanguages}
                            wording={wording}
                            showSuggestion={visitorGetsSuggestion}
                        />
                    )}
                    {view === ViewMap.Negative && (
                        <Negative
                            locationSurveyId={locationSurveyId}
                            view={view}
                            location={previewLocation}
                            languages={surveyLanguages}
                            wording={wording}
                        />
                    )}
                </PhonePreviewFrame>
            )}
        </>
    );
};

export default Preview;
